import apiClient from './client';

export interface StoreSummary {
  id: number;
  name: string;
  /** 브랜드 앱 번들 ID (매장 식별자 겸 Apple 로그인·결제 복귀 스킴) */
  bundle_id: string;
  /** 매장의 간편결제 환경 */
  payment_mode: PaymentEnv;
  created_at: string;
  admin_emails: string | null;
  /** 매장의 현재 결제 환경(payment_mode) 기준 키 등록 여부. MySQL이 0/1(키 행이 없으면 null)로 준다. */
  kakao_configured: number | null;
  naver_configured: number | null;
}

export interface CreateStorePayload {
  name: string;
  bundleId: string;
  adminEmail: string;
  adminPassword: string;
}

export const listStores = async (): Promise<StoreSummary[]> => {
  const { data } = await apiClient.get<StoreSummary[]>('/admin/stores');
  return data;
};

export const createStore = async (payload: CreateStorePayload): Promise<{ storeId: number }> => {
  const { data } = await apiClient.post<{ storeId: number }>('/admin/stores', payload);
  return data;
};

export type PaymentEnv = 'sandbox' | 'production';

/** 매장 PG 키. 시크릿은 서버가 내려주지 않고 등록 여부(*Set)만 알려준다. */
export interface PaymentCredentials {
  env: PaymentEnv;
  kakaoCid: string;
  kakaoSecretKeySet: boolean;
  naverPartnerId: string;
  naverClientId: string;
  naverClientSecretSet: boolean;
  naverChainId: string;
  updatedAt: string | null;
}

export interface PaymentCredentialsResponse {
  /** 매장이 지금 결제에 쓰는 환경(payment_mode) */
  currentEnv: PaymentEnv;
  credentials: PaymentCredentials[];
}

export type SecretField = 'kakaoSecretKey' | 'naverClientSecret';

export interface UpdatePaymentCredentialsPayload {
  kakaoCid: string;
  naverPartnerId: string;
  naverClientId: string;
  naverChainId: string;
  /** 비워 두면 기존 값을 유지한다 */
  kakaoSecretKey?: string;
  naverClientSecret?: string;
  clearSecrets?: SecretField[];
}

export const getPaymentCredentials = async (storeId: number): Promise<PaymentCredentialsResponse> => {
  const { data } = await apiClient.get<PaymentCredentialsResponse>(`/admin/stores/${storeId}/payment-credentials`);
  return data;
};

export const updatePaymentCredentials = async (
  storeId: number,
  env: PaymentEnv,
  payload: UpdatePaymentCredentialsPayload
): Promise<PaymentCredentials> => {
  const { data } = await apiClient.put<PaymentCredentials>(
    `/admin/stores/${storeId}/payment-credentials/${env}`,
    payload
  );
  return data;
};

/** 매장의 결제 환경 전환. 전환할 환경의 키가 하나도 등록돼 있지 않으면 409 */
export const setPaymentMode = async (storeId: number, mode: PaymentEnv): Promise<{ mode: PaymentEnv }> => {
  const { data } = await apiClient.put<{ mode: PaymentEnv }>(`/admin/stores/${storeId}/payment-mode`, { mode });
  return data;
};

export type PolicyType = 'privacy' | 'terms';
export type PolicyFormat = 'html' | 'text';

/** 매장별 개인정보처리방침/이용약관. url은 앱·앱스토어에 쓰는 공개 주소 */
export interface StorePolicy {
  type: PolicyType;
  url: string;
  content: string;
  format: PolicyFormat;
  updatedAt: string | null;
}

export const getStorePolicies = async (storeId: number): Promise<StorePolicy[]> => {
  const { data } = await apiClient.get<StorePolicy[]>(`/admin/stores/${storeId}/policies`);
  return data;
};

/** content를 비우면 문서가 삭제된다 */
export const updateStorePolicy = async (
  storeId: number,
  type: PolicyType,
  payload: { content: string; format: PolicyFormat }
): Promise<Omit<StorePolicy, 'url'>> => {
  const { data } = await apiClient.put<Omit<StorePolicy, 'url'>>(`/admin/stores/${storeId}/policies/${type}`, payload);
  return data;
};

export interface SignupPayload {
  email: string;
  password: string;
}

export interface SignupResponse {
  isMasterAdmin: boolean;
  approvalStatus: 'approved' | 'pending';
}

export const signup = async (payload: SignupPayload): Promise<SignupResponse> => {
  const { data } = await apiClient.post<SignupResponse>('/admin/signup', payload);
  return data;
};

export interface PendingAdmin {
  id: number;
  email: string;
  created_at: string;
}

export const listPendingAdmins = async (): Promise<PendingAdmin[]> => {
  const { data } = await apiClient.get<PendingAdmin[]>('/admin/pending-admins');
  return data;
};

export const approveAdmin = async (id: number): Promise<void> => {
  await apiClient.patch(`/admin/pending-admins/${id}/approve`);
};

export const rejectAdmin = async (id: number): Promise<void> => {
  await apiClient.patch(`/admin/pending-admins/${id}/reject`);
};
